import React, { useState, useEffect } from "react";
import moment from "moment";
import { Box, Button, InputAdornment, Typography } from "@mui/material";
import Form from "react-bootstrap/Form";
import momentTimezone from "moment-timezone";
import { toast } from "react-toastify";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import ImageUploading from "react-images-uploading";
import { BsUpload } from "react-icons/bs";
import SelectOrAddCurrency from "../../components/SelectOrAddCurrency";
import currencies from "../../../constants/currencies.json";
import uploadDocumentEvents from "../../../api/uploadDocumentEvents";

import "react-datetime/css/react-datetime.css";
import "react-toastify/dist/ReactToastify.css";
import { DATE_TIME_DISPLAY } from "../../../common/dateTime";

const LotForm = ({
    mode = "create",
    eventItemInfo,
    setEventItemInfo,
    eventDetails = {},
    selectedCurrency,
    setSelectedCurrency,
    options,
    setOptions,
    setIsLoading,
    onSubmit,
    classes,
    onCancel,
    initialData = {}, // Used in edit mode
    children,
}) => {
    const maxNumber = 5;
    const isEnglishAuction = eventDetails.eventFormat === "ENGLISH_AUCTION";
    const isJapaneseAuction = eventDetails.eventFormat === "JAPANESE_AUCTION";
    const isDutchAuction = eventDetails.eventFormat === "DUTCH_AUCTION";

    const [startDateNew, setStartDate] = useState(null);
    const [endDateNew, setEndDate] = useState(null);
    const [images, setImages] = useState([]);

    useEffect(() => {
        if (!options.length) {
            const currencyArray = Object.entries(currencies[0]).map(([key, value]) => ({
                value: key,
                label: value.code,
            }));
            setOptions(currencyArray);
        }
    }, [options, setOptions]);

    useEffect(() => {
        setEventItemInfo((prev) => ({
            ...prev,
            currency: selectedCurrency,
        }));
    }, [selectedCurrency, setEventItemInfo]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        if (name === "minimumAmount" && value < 0) return;
        setEventItemInfo((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleUnitChange = (event) => {
        if (event.target.value.match("^[a-zA-Z ]*$") != null) {
            handleChange(event);
        }
    };

    const onImageChange = (imageList) => {
        setImages(imageList);
        imageUpload(imageList);
    };

    const imageUpload = async (imageList) => {
        try {
            setIsLoading(true);
            const uploadedImageIds = [];
            for (let image of imageList) {
                const payload = { document: image["file"] };
                const { data } = await uploadDocumentEvents(payload);
                if (data) {
                    uploadedImageIds.push(data.documentId);
                }
            }
            setEventItemInfo((prev) => ({
                ...prev,
                eventItemImages: uploadedImageIds,
            }));
            toast.success("Item Image Uploaded Successfully!");
        } catch (error) {
            console.error("Image upload error:", error);
            toast.error("Something went wrong during image upload!");
        } finally {
            setIsLoading(false);
        }
    };

    const shouldRenderMinimumAmountField = (eventFormat, eventType) => {
        if (eventFormat === "DUTCH_AUCTION" && eventType === "FORWARD_AUCTION") return true;
        if (eventFormat === "JAPANESE_AUCTION" && eventType === "REVERSE_AUCTION") return true;
        return false;
    };

    const handleSubmitInternal = (e) => {
        e.preventDefault();
        
        const payload = { ...eventItemInfo };
        payload.quantity = Number(payload.quantity);
        payload.startPrice = Number(payload.startPrice);
        payload.increment = Number(payload.increment);
        payload.lastMinuteBid = "123";

        // Handle Start Date
        if (startDateNew) {
            let startDateMoment = moment(startDateNew.$d, "dd/MM/YYYY HH:mm:ss ZZ");
            payload.startDateAndTime = startDateMoment.format("YYYY-MM-DD HH:mm:ss");
        }

        // Handle End Date (English Only)
        if (isEnglishAuction) {
            delete payload.minimumAmount;
            if (endDateNew) {
                let endDateMoment = moment(endDateNew.$d, "dd/MM/YYYY HH:mm:ss ZZ");
                payload.endDateAndTime = endDateMoment.format("YYYY-MM-DD HH:mm:ss");
            }
            if (payload.endDateAndTime) {
                payload.endDateAndTime = momentTimezone(payload.endDateAndTime).utc();
            }
        } else {
            delete payload.endDateAndTime;
        }

        if (payload.startDateAndTime) {
            payload.startDateAndTime = momentTimezone(payload.startDateAndTime).utc();
        }

        // Dutch and Japanese Specific Minimum Amount Logic
        if (!shouldRenderMinimumAmountField(eventDetails.eventFormat, eventDetails.eventType)) {
            delete payload.minimumAmount;
        } else {
            payload.minimumAmount = Number(payload.minimumAmount) || 0;
        }

        // Japanese Auction Specifics
        if (isJapaneseAuction) {
            payload.maxMissedBids = payload.maxMissedBids || 3;
        } else {
            payload.maxMissedBids = null;
        }

        // Time Interval logic
        if (isEnglishAuction) {
            delete payload.timeInterval;
        }

        onSubmit(payload);
    };

    return (
        <form onSubmit={handleSubmitInternal}>
            <Box className={classes.accordanContent}>
                <div className={classes.formHead}>
                    <h5 className="head">Item Detail</h5>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <Box className={classes.forms}>
                            <Form.Group className="mb-3">
                                <Form.Label className={classes.label}>Lot Number</Form.Label>
                                <Form.Control
                                    className={classes.input}
                                    type="text"
                                    name="lotNumber"
                                    onChange={handleChange}
                                    value={eventItemInfo.lotNumber || ""}
                                    required
                                />
                            </Form.Group>
                        </Box>
                        <Box className={classes.forms}>
                            <Form.Group className="mb-3">
                                <Form.Label className={classes.label}>Short Description (Limit 50 characters)</Form.Label>
                                <Form.Control
                                    className={classes.input}
                                    as="textarea"
                                    name="shortDescription"
                                    style={{ height: "24px" }}
                                    onChange={handleChange}
                                    value={eventItemInfo.shortDescription || ""}
                                    maxLength={50}
                                    required
                                />
                            </Form.Group>
                        </Box>
                        <Box className={classes.forms}>
                            <Form.Group className="mb-3">
                                <Form.Label className={classes.label}>Remark (if any)</Form.Label>
                                <Form.Control
                                    className={classes.input}
                                    type="text"
                                    name="remarks"
                                    onChange={handleChange}
                                    value={eventItemInfo.remarks || ""}
                                />
                            </Form.Group>
                        </Box>
                    </div>
                    <div className="col-md-6">
                        <Box className={classes.forms}>
                            <Form.Label className={classes.label}>Long Description (Limit 250 characters)</Form.Label>
                            <Form.Control
                                className={classes.input}
                                as="textarea"
                                name="longDescription"
                                onChange={handleChange}
                                value={eventItemInfo.longDescription || ""}
                                style={{ height: "130px" }}
                                maxLength={250}
                                required
                            />
                        </Box>
                    </div>
                </div>

                <div className={classes.formHead}>
                    <h5 className="heading">Rate and Amount</h5>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <Box className={classes.forms}>
                            <Form.Group className="mb-3">
                                <Form.Label className={classes.label}>Qty</Form.Label>
                                <Form.Control
                                    className={classes.input}
                                    type="number"
                                    name="quantity"
                                    min="1"
                                    onChange={handleChange}
                                    value={eventItemInfo.quantity || ""}
                                    required
                                />
                            </Form.Group>
                        </Box>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Label className={classes.label}>Currency</Form.Label>
                                <SelectOrAddCurrency
                                    value={selectedCurrency}
                                    setValue={setSelectedCurrency}
                                    options={options}
                                    setOptions={setOptions}
                                />
                            </div>
                            <div className="col-md-6">
                                <Box className={classes.forms}>
                                    <Form.Label className={classes.label}>
                                        Start Price
                                        {eventDetails.previewPeriod === "MANDATORY" && (
                                            <Box sx={{ fontSize: "12px", color: "red", fontWeight: "800", mb: 1 }}>
                                                Mandatory first bid: bidders don't see this until their first bid, and it isn't used for bidding
                                                {eventDetails.incrementBidAmountBy === "PERCENTAGE" &&
                                                    eventDetails.variableIncrementDecrement === "NO" &&
                                                    " (except fixed percentage steps, which are counted from the start price)"}
                                            </Box>
                                        )}
                                    </Form.Label>
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            className={classes.input}
                                            type="number"
                                            name="startPrice"
                                            min="0"
                                            step={"0.01"}
                                            onChange={handleChange}
                                            value={eventItemInfo.startPrice || ""}
                                            required
                                        />
                                    </Form.Group>
                                </Box>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <Form.Group className="mb-3">
                            <Form.Label className={classes.label}>Unit</Form.Label>
                            <Form.Control
                                className={classes.input}
                                type="text"
                                name="unit"
                                placeholder="Unit"
                                onChange={handleUnitChange}
                                value={eventItemInfo.unit || ""}
                                required
                            />
                        </Form.Group>
                        <div className="row">
                            <div
                                className={`${!isEnglishAuction && eventDetails.eventType === "REVERSE_AUCTION"
                                    ? "col-md-6"
                                    : "col-md-12"
                                    } `}
                            >
                                <Form.Group className="mb-3">
                                    <Form.Label className={classes.label}>
                                        {isDutchAuction
                                            ? eventDetails.eventType === "REVERSE_AUCTION"
                                                ? "Increment"
                                                : "Decrement"
                                            : eventDetails.eventType === "REVERSE_AUCTION"
                                                ? "Decrement"
                                                : "Increment"}
                                    </Form.Label>
                                    <div style={{ position: "relative" }}>
                                        <Form.Control
                                            className={classes.input}
                                            type="number"
                                            name="increment"
                                            step={"0.01"}
                                            onChange={handleChange}
                                            value={eventItemInfo.increment || ""}
                                            inputMode="decimal"
                                            required
                                            min={0.0}
                                            style={{ paddingRight: "30px" }}
                                        />
                                        <span
                                            style={{
                                                position: "absolute",
                                                right: "10px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                color: "#6c757d",
                                                pointerEvents: "none",
                                            }}
                                        >
                                            {eventDetails.incrementBidAmountBy === "PERCENTAGE" ? "%" : eventItemInfo.unit}
                                        </span>
                                    </div>
                                </Form.Group>
                            </div>

                            {shouldRenderMinimumAmountField(eventDetails.eventFormat, eventDetails.eventType) && (
                                <div className="col-md-6">
                                    <Form.Label className={classes.label}>Minimum Amount</Form.Label>
                                    <TextField
                                        className={classes.input}
                                        type="number"
                                        size="small"
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
                                        name="minimumAmount"
                                        InputProps={{
                                            inputProps: { min: 0 },
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {eventItemInfo?.currency || ""}
                                                </InputAdornment>
                                            ),
                                        }}
                                        onChange={handleChange}
                                        value={eventItemInfo.minimumAmount ?? 0}
                                        fullWidth
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={classes.formHead}>
                    <h5 className="time">Timing</h5>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <Form.Label className={classes.label}>
                            Start Date & Time{" "}
                            {eventDetails.bidStartDateTime && (
                                <Box sx={{ fontSize: "12px", color: "red", fontWeight: "800", mb: 1 }}>
                                    {`>= Auction Start:`} {moment(eventDetails.bidStartDateTime).format(DATE_TIME_DISPLAY)}
                                </Box>
                            )}
                            {mode === "edit" && initialData.startDateAndTime && (
                                <Box sx={{ fontSize: "12px", color: "red", fontWeight: "800", mb: 1 }}>
                                    {`>`} {moment(initialData.startDateAndTime).format(DATE_TIME_DISPLAY)}
                                </Box>
                            )}
                        </Form.Label>
                        <br />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                className="datePicker"
                                renderInput={(props) => <TextField {...props} />}
                                value={startDateNew || (eventItemInfo.startDateAndTime ? moment(eventItemInfo.startDateAndTime) : null)}
                                onChange={(newValue) => setStartDate(newValue)}
                                minDate={new Date()}
                            />
                        </LocalizationProvider>
                    </div>

                    {isEnglishAuction && (
                        <div className="col-md-6 mob-margin">
                            <Form.Label className={classes.label}>
                                End Date & Time{" "}
                                {eventDetails.bidEndDateTime && (
                                    <Box sx={{ fontSize: "12px", color: "red", fontWeight: "800", mb: 1 }}>
                                        {`<= Auction End:`} {moment(eventDetails.bidEndDateTime).format(DATE_TIME_DISPLAY)}
                                    </Box>
                                )}
                            </Form.Label>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    className="datePicker"
                                    renderInput={(props) => <TextField {...props} />}
                                    value={endDateNew || (eventItemInfo.endDateAndTime ? moment(eventItemInfo.endDateAndTime) : null)}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    minDate={new Date()}
                                />
                            </LocalizationProvider>
                        </div>
                    )}

                    {isJapaneseAuction && (
                        <div className="col-md-6">
                            <Form.Label className={classes.label}>Max missed bids</Form.Label>
                            <TextField
                                className={classes.input}
                                type="number"
                                size="small"
                                required={isJapaneseAuction}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
                                name="maxMissedBids"
                                InputProps={{ inputProps: { min: 1 } }}
                                onChange={handleChange}
                                value={eventItemInfo.maxMissedBids || ""}
                                fullWidth
                            />
                        </div>
                    )}
                </div>

                {!isEnglishAuction && (
                    <div className="row mt-3">
                        <div className="col-md-6">
                            <Form.Label className={classes.label}>Time interval</Form.Label>
                            <TextField
                                className={classes.input}
                                type="number"
                                size="small"
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 } }}
                                name="timeInterval"
                                InputProps={{
                                    inputProps: { min: 1 },
                                    endAdornment: <InputAdornment position="end">min.</InputAdornment>,
                                }}
                                onChange={handleChange}
                                value={eventItemInfo.timeInterval || ""}
                                required
                                fullWidth
                            />
                        </div>
                    </div>
                )}

                <div className={classes.formHead}>
                    <h5 className="headings">Image and Document</h5>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className={classes.uploader}>
                            <p className={classes.imageSize} style={{ fontSize: "12px" }}>Dimension: 260px X 200px</p>
                            <div className="row">
                                {mode === "edit" && initialData?.eventItemImagesUrl?.length > 0 && (
                                    <div className="row mb-3">
                                        <p>Old Images</p>
                                        {initialData.eventItemImagesUrl.map((image, index) => (
                                            <div key={index} className="col-md-3">
                                                <div className="image-item">
                                                    <img src={image} alt="" width="100" className={classes.imagesStyle} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <ImageUploading
                                    multiple
                                    value={images}
                                    onChange={onImageChange}
                                    maxNumber={maxNumber}
                                    dataURLKey="data_url"
                                    acceptType={["jpg", "jpeg", "png"]}
                                >
                                    {({ imageList, onImageUpload, dragProps, errors }) => (
                                        <>
                                            <div className="upload__image-wrapper row">
                                                <div className="col-md-4">
                                                    <Button
                                                        variant="contained"
                                                        component="label"
                                                        className={classes.upload}
                                                        sx={{
                                                            display: "block",
                                                            textAlign: "center",
                                                            padding: "1.5em",
                                                        }}
                                                        onClick={onImageUpload}
                                                        {...dragProps}
                                                    >
                                                        <BsUpload className={classes.uploadIcon} />
                                                        <br />
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    color: "#000",
                                                                    fontSize: "12px",
                                                                    marginTop: "10px",
                                                                }}
                                                            >
                                                                Choose Image to upload
                                                            </Typography>
                                                        </Box>
                                                    </Button>
                                                </div>
                                                <div className="col-md-8">
                                                    <div className="row">
                                                        {imageList.map((image, index) => (
                                                            <div key={index} className="col-md-2">
                                                                <div className="image-item">
                                                                    <img src={image["data_url"]} alt="" width="100" className={classes.imagesStyle} />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            {errors && (
                                                <div className="mt-2 text-danger" style={{ fontSize: "12px" }}>
                                                    {errors.maxNumber && <span>Number of selected images exceed maxNumber</span>}
                                                    {errors.acceptType && <span>Your selected file type is not allowed</span>}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </ImageUploading>
                            </div>
                        </div>
                    </div>
                </div>

                {children}
            </Box>
        </form>
    );
};

export default LotForm;
